import { useNotification } from "../../../context/notification-provider";
import { useDialogs } from "../../dialog/use-dialog";
import { PartialBlob } from "../types";
import { isBlobArchived, isBlobDeleted } from "../utils/blob-status";
import {
  handleDeleteFiles,
  handleDownloadFile,
  handleRehydrateFiles,
} from "../utils/file-actions-handlers";

interface UseFileActionsReturn {
  onDeleteFile: (blob: PartialBlob, onSuccess: () => void) => Promise<void>;
  onDeleteFiles: (blobs: PartialBlob[], onSuccess: () => void) => Promise<void>;
  onDownloadFile: (blob: PartialBlob) => Promise<void>;
  onRehydrateFile: (blob: PartialBlob, onSuccess: () => void) => Promise<void>;
  onRehydrateFiles: (
    blobs: PartialBlob[],
    onSuccess: () => void,
  ) => Promise<void>;
}

// eslint-disable-next-line import/prefer-default-export
export const useFileActions = (): UseFileActionsReturn => {
  const { showNotification } = useNotification();
  const { confirm } = useDialogs();

  const onDeleteFile = async (blob: PartialBlob, onSuccess: () => void) => {
    try {
      const result = await confirm(
        `Are you sure you want to delete "${blob.name}"? This action cannot be undone.`,
        {
          title: "Confirm File Deletion",
          okText: "Delete",
          cancelText: "Cancel",
          severity: "error",
        },
      );

      if (result === true) {
        await handleDeleteFiles([blob]);
        showNotification({
          message: `File "${blob.name}" deleted successfully.`,
          severity: "success",
          duration: 3000,
        });
        onSuccess();
      }
    } catch (deleteError) {
      showNotification({
        message: `Failed to delete file. ${(deleteError as Error).message}`,
        severity: "error",
        duration: 3000,
      });
    }
  };

  const onDeleteFiles = async (blobs: PartialBlob[], onSuccess: () => void) => {
    try {
      const result = await confirm(
        `Are you sure you want to delete ${blobs.length} selected file(s)? This action cannot be undone.`,
        {
          title: "Confirm File Deletion",
          okText: "Delete",
          cancelText: "Cancel",
          severity: "error",
        },
      );

      if (result === true) {
        await handleDeleteFiles(blobs);
        showNotification({
          message: `${blobs.length} file(s) deleted successfully.`,
          severity: "success",
          duration: 3000,
        });
        onSuccess();
      }
    } catch (deleteError) {
      showNotification({
        message: `Failed to delete files. ${(deleteError as Error).message}`,
        severity: "error",
        duration: 3000,
      });
    }
  };

  const onDownloadFile = async (blob: PartialBlob) => {
    if (isBlobDeleted(blob)) {
      showNotification({
        message: `"${blob.name}" has been deleted and cannot be downloaded.`,
        severity: "info",
        duration: 3000,
      });
      return;
    }

    if (isBlobArchived(blob)) {
      showNotification({
        message: `"${blob.name}" is archived. Rehydrate it before downloading.`,
        severity: "info",
        duration: 3000,
      });
      return;
    }

    try {
      await handleDownloadFile(blob);
      showNotification({
        message: `File "${blob.name}" downloaded successfully.`,
        severity: "success",
        duration: 3000,
      });
    } catch (downloadError) {
      showNotification({
        message: `Failed to download file. ${(downloadError as Error).message}`,
        severity: "error",
        duration: 3000,
      });
    }
  };

  const onRehydrateFile = async (blob: PartialBlob, onSuccess: () => void) => {
    try {
      const result = await confirm(
        `Rehydrate "${blob.name}" from archive storage? This can take several hours to complete.`,
        {
          title: "Confirm Rehydration",
          okText: "Rehydrate",
          cancelText: "Cancel",
          severity: "warning",
        },
      );

      if (result === true) {
        await handleRehydrateFiles([blob]);
        showNotification({
          message: `Rehydration of "${blob.name}" has started. This can take several hours.`,
          severity: "success",
          duration: 3000,
        });
        onSuccess();
      }
    } catch (rehydrateError) {
      showNotification({
        message: `Failed to rehydrate file. ${(rehydrateError as Error).message}`,
        severity: "error",
        duration: 3000,
      });
    }
  };

  const onRehydrateFiles = async (
    blobs: PartialBlob[],
    onSuccess: () => void,
  ) => {
    try {
      const result = await confirm(
        `Rehydrate ${blobs.length} selected file(s) from archive storage? This can take several hours to complete.`,
        {
          title: "Confirm Rehydration",
          okText: "Rehydrate",
          cancelText: "Cancel",
          severity: "warning",
        },
      );

      if (result === true) {
        await handleRehydrateFiles(blobs);
        showNotification({
          message: `Rehydration of ${blobs.length} file(s) has started. This can take several hours.`,
          severity: "success",
          duration: 3000,
        });
        onSuccess();
      }
    } catch (rehydrateError) {
      showNotification({
        message: `Failed to rehydrate files. ${(rehydrateError as Error).message}`,
        severity: "error",
        duration: 3000,
      });
    }
  };

  return {
    onDeleteFile,
    onDeleteFiles,
    onDownloadFile,
    onRehydrateFile,
    onRehydrateFiles,
  };
};
